import { z } from 'zod';
// Mag analytics — event shapes shared between the mag.js SDK, the api
// ingest endpoint, and the dashboard read layer. The SDK posts events
// matching analyticsEventSchema; the api validates them, stamps the
// server-side fields, and writes to the events table.
//
// Source of truth for "what an event looks like" — when we wire
// ClickHouse later, the column set is derived from this shape.
export const analyticsEventTypeSchema = z.enum([
    // Fired once per pageview. Carries url, referrer, attribution.
    'page_view',
    // Fired on a meaningful click (button, link, or any element with a
    // [data-mag-track] attribute). Carries selector, text, x/y coords.
    'click',
    // Fired when the visitor crosses 25/50/75/100% of page depth.
    // Carries depthPct (one of 25/50/75/100) and time-on-page.
    'scroll',
    // Founder-defined goal — fired via window.mag.track('signup', { ... }).
    // Carries name + arbitrary value payload.
    'conversion',
    // Identity link from window.mag.identify(userId, traits). Carries
    // user_id + traits. Doesn't show up in heatmaps; powers funnel
    // joining + LTV reads.
    'identify',
]);
/** UTM-style attribution payload, captured on first page_view per
 *  session and replayed onto subsequent events so downstream queries
 *  can ask "which ad source drove this conversion". */
export const analyticsAttributionSchema = z.object({
    utm_source: z.string().max(120).optional(),
    utm_medium: z.string().max(120).optional(),
    utm_campaign: z.string().max(200).optional(),
    utm_term: z.string().max(200).optional(),
    utm_content: z.string().max(200).optional(),
    /** Facebook / Meta click ID. */
    fbclid: z.string().max(200).optional(),
    /** Google click ID. */
    gclid: z.string().max(200).optional(),
    /** TikTok click ID. */
    ttclid: z.string().max(200).optional(),
    /** Platform ad id, captured off the landing URL as `mag_ad_id`.
     *
     *  THIS FIELD'S ABSENCE IS WHY ad_id WAS ALWAYS EMPTY. eventInputToRow has
     *  read `attribution.ad_id` since the table existed (packages/clickhouse/
     *  src/events.ts:272) and buildFinalizeInsert carries it onto the session
     *  row — the write side has been waiting the whole time. But this schema
     *  never declared the key, and analyticsIngestBodySchema.safeParse
     *  (apps/api/src/analytics.ts:574) is a zod object parse, which STRIPS what
     *  it does not declare. An SDK that sent ad_id would have had it deleted at
     *  the API boundary, with no error and no missing-field warning.
     *
     *  The standing explanation — that ad_id is empty because the async
     *  gclid→ad-id resolver the analytics design doc describes was never built
     *  — is one reason among several, and not the load-bearing one. Spec
     *  5e5cfea0.
     *
     *  Named ad_id rather than mag_ad_id because that is the column it lands
     *  in; the URL parameter keeps the mag_ prefix so it cannot collide with
     *  anything already on a founder's page. */
    ad_id: z.string().max(200).optional(),
});
/** What the SDK POSTs. The api stamps server-side fields (received_at,
 *  country, ip-derived hints) before writing. */
export const analyticsEventPayloadSchema = z.object({
    /** Project key the founder pasted into their script tag. Format
     *  `mag_<26 base36 chars>` — see api project provisioning. */
    project_key: z.string().regex(/^mag_[a-z0-9]{16,40}$/, 'project_key must be mag_<id>'),
    type: analyticsEventTypeSchema,
    /** Random anonymous id assigned once per browser, persisted in
     *  localStorage. Lets us join events to a person without a cookie. */
    anon_id: z.string().min(8).max(64),
    /** Session id, rolls every 30 minutes of inactivity or on the next
     *  page_view after that window. */
    session_id: z.string().min(8).max(64),
    /** Wall-clock from the visitor's browser. The api also stamps a
     *  trusted received_at on insert — use received_at for ordering. */
    client_ts: z.string(), // ISO datetime
    /** Full URL of the page the event fired on. */
    url: z.string().url(),
    /** Page path only (URL without origin/search), denormalized for
     *  cheap heatmap queries. */
    path: z.string().min(1).max(2048),
    /** Optional referrer (`document.referrer`). */
    referrer: z.string().max(2048).optional(),
    attribution: analyticsAttributionSchema.optional(),
    /** Event-specific payload — discriminated below. */
    data: z.record(z.string(), z.unknown()).optional(),
    /** Raw browser user-agent string. The api parses this at ingest
     *  into device_type / browser / os so the dashboard can render
     *  chips without re-parsing. SDK clients fill it from
     *  `navigator.userAgent`; capped to 1k so a pathological UA can't
     *  blow up the payload. */
    user_agent: z.string().max(1024).optional(),
});
/* ── The SDK's real wire facts ──────────────────────────────────────────
 *
 * These live HERE, in a package both apps/dashboard and packages/agents can
 * import, because they were previously only in the dashboard's
 * analytics-snippet.ts — out of reach of the agents that WRITE INSTALL
 * INSTRUCTIONS FOR FOUNDERS.
 *
 * The consequence was not theoretical. mag-campaign-synthesizer handed
 * founders' coding agents a snippet naming https://cdn.magig.app/v1/track.js,
 * `data-magig-key`, `window.magig` and `ingest.magig.app/ingest/v1/track`.
 * Measured 2026-10-07: cdn.magig.app and ingest.magig.app DO NOT RESOLVE, the
 * attribute is data-project, the global is window.mag, and /ingest/v1/track is
 * the SERVER endpoint which 401s any browser. Two paying brands built against
 * that prompt and neither one's conversions ever arrived; one of them debugged
 * and fixed a real bug on their own side chasing it.
 *
 * Anything that tells a human or a model how to install the tag must read
 * these, and a test pins the synthesizer prompt against them.
 */
/** Where the browser SDK is served. */
export const SDK_SCRIPT_URL = 'https://assets.magig.app/mag.js';
/** Attribute carrying the brand's PUBLIC project key on the loader tag. */
export const SDK_PROJECT_ATTR = 'data-project';
/** The global the SDK defines. NOT `magig`. */
export const SDK_GLOBAL = 'mag';
/** Browser batch ingest. Public project key in the body; Origin-checked
 *  against the tracker's allowed_hosts. */
export const SDK_BROWSER_INGEST_URL = 'https://api.magig.app/ingest/v1';
/** Server-to-server ingest. Requires the SECRET write key in an
 *  X-Mag-Write-Key header and sends no Origin, which is exactly why a browser
 *  must never be pointed at it. */
export const SDK_SERVER_INGEST_URL = 'https://api.magig.app/ingest/v1/track';
/** Ingestion request body — the SDK batches 1+ events into one POST. */
export const analyticsIngestBodySchema = z.object({
    events: z.array(analyticsEventPayloadSchema).min(1).max(50),
});
/** Server-side ingest body — what a founder's webhook POSTs to
 *  /ingest/v1/track when authenticating with X-Mag-Write-Key. Single
 *  event per request (no batching — server-side calls are typically
 *  one-off webhook callbacks like payment.succeeded). Distinct from the
 *  client batch shape: no session_id (server events have no browser
 *  session), no url/path (no page context), no user_agent. The event
 *  name lives at the top level (not in `data.name`) so the wire shape
 *  reads like a typical analytics SDK call. */
export const analyticsServerIngestSchema = z.object({
    /** Event name, lower_snake_case by convention so client + server
     *  events join cleanly in the funnel. */
    event: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z][a-z0-9_]*$/, 'event must be lower_snake_case'),
    /** Stitches this server event to the visitor's anon_id from the
     *  client SDK. The founder is responsible for threading the
     *  distinct_id through their backend (typically: SDK exposes
     *  getDistinctId in the browser → captured at signup → stored on the
     *  user row → re-used by every server-side event for that user). */
    distinct_id: z.string().min(8).max(64),
    /** Event-specific payload — same shape as client `data`. */
    properties: z.record(z.string(), z.unknown()).optional(),
    /** Optional explicit timestamp from the source system (e.g.
     *  payment.succeeded webhooks include the gateway's event time —
     *  more accurate than the moment our api received the relayed POST).
     *  If omitted, the api stamps received_at. */
    client_ts: z.string().optional(),
    /** Click ids and utm values captured when the visitor ARRIVED, forwarded
     *  by the founder's backend alongside the conversion.
     *
     *  Without this a server-side conversion is invisible to google. The
     *  uploader selects on `gclid != ''` (conversionsWithGclid), server rows
     *  never carried one, and the storage layer has read gclid out of
     *  `attribution` all along — this path simply never passed it. The
     *  consequence was that a brand could instrument their outcome correctly,
     *  server-side on confirmed state, and still have it unable to reach the
     *  bidding goal.
     *
     *  Same key names the browser SDK uses, so a founder forwards what they
     *  captured rather than translating: gclid, fbclid, ttclid, ad_id,
     *  utm_source, utm_medium, utm_campaign, utm_content, utm_term. Unknown
     *  keys are ignored rather than rejected — an attribution blob is
     *  copied from a landing page, not hand-written. */
    attribution: z.record(z.string(), z.unknown()).optional(),
});
// ─── Read shapes (dashboard) ─────────────────────────────────────────────
export const analyticsProjectSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    /** The literal mag_<id> string the founder pastes in their script
     *  tag. Stable forever. */
    key: z.string(),
    /** Founder-set label ("magig.app", "blog", etc.). */
    name: z.string(),
    /** Allowed origins for ingestion — at least one. Default to the
     *  founder's brand brain site URL on creation. */
    allowedHosts: z.array(z.string()),
    createdAt: z.string(),
    /** Set once an event has been seen; the dashboard surfaces a green
     *  pill when this is non-null. */
    firstEventAt: z.string().nullable(),
    /** True iff the founder has generated a server-side write key for
     *  this tracker. We never expose the key itself in reads — only
     *  whether one exists, so the dashboard can render "generate" vs
     *  "rotate" without ever shipping the secret over the wire after the
     *  one-shot reveal at creation time. */
    hasWriteKey: z.boolean(),
    /** Whether the stored key can be shown again. False for a legacy key
     *  generated before the ciphertext column existed — its plaintext is
     *  unrecoverable, so the founder must rotate to get a readable one. */
    writeKeyRecoverable: z.boolean(),
    /** When the current write key was generated. Null when no key is
     *  set. Used in the dashboard label "set on <date>". */
    writeKeyCreatedAt: z.string().nullable(),
});
export const analyticsEventRowSchema = z.object({
    /** Opaque per-row identifier. PG events used a uuid PK; CH-backed
     *  rows synthesize a stable id from the row's natural key (received
     *  ts + visitor + session + type + url) so React list keys still work
     *  without surfacing column-store internals. Treat as opaque. */
    id: z.string(),
    projectId: z.string().uuid(),
    type: analyticsEventTypeSchema,
    anonId: z.string(),
    sessionId: z.string(),
    url: z.string(),
    path: z.string(),
    referrer: z.string().nullable(),
    /** What the SDK sent verbatim (minus project_key which is folded into
     *  projectId). Useful for the dashboard's "recent events" debug feed. */
    data: z.record(z.string(), z.unknown()).nullable(),
    attribution: analyticsAttributionSchema.nullable(),
    /** Server-stamped country from MaxMind GeoLite2 lookup. Null until
     *  the geo lookup is wired (Phase 1 §10 of analytics-design.md). */
    country: z.string().nullable(),
    clientTs: z.string(),
    receivedAt: z.string(),
    /** Device family parsed from user_agent at ingest. Null for rows
     *  ingested before the SDK started sending UA. */
    deviceType: z.string().nullable(),
    browser: z.string().nullable(),
    os: z.string().nullable(),
});
/** Per-path click count, rolled up by the api for the dashboard's
 *  "where are people clicking" panel. */
export const analyticsPathClickCountSchema = z.object({
    path: z.string(),
    clicks: z.number().int().min(0),
    /** Distinct anon_ids that clicked on this path in the window. */
    uniqueVisitors: z.number().int().min(0),
});
/** Top-N rollup of a single facet (device type, browser family,
 *  referrer host, etc.). `share` is 0..1 of the total inside the
 *  window, useful for inline bar widths. */
export const analyticsTopFacetSchema = z.object({
    value: z.string(),
    count: z.number().int().min(0),
    share: z.number().min(0).max(1),
});
/** The audience-makeup rollup the dashboard renders as a single tile
 *  cluster on the live view. Each list is capped server-side at 6. */
export const analyticsAudienceSchema = z.object({
    windowDays: z.number().int().min(1),
    devices: z.array(analyticsTopFacetSchema),
    browsers: z.array(analyticsTopFacetSchema),
    referrers: z.array(analyticsTopFacetSchema),
    countries: z.array(analyticsTopFacetSchema),
});
/** Daily visit counts for the trends chart. The api fills zero days
 *  so the array is always exactly the requested window length, ordered
 *  oldest-first. */
export const analyticsVisitsByDaySchema = z.object({
    /** YYYY-MM-DD in the brand's local rendering — but stamped from
     *  server time, not the visitor's. Dashboard just renders verbatim. */
    date: z.string(),
    /** page_view events that day. */
    pageViews: z.number().int().min(0),
    /** Distinct anon_ids that fired ANY event that day. */
    uniqueVisitors: z.number().int().min(0),
});
/** One active visitor in the recent window — the "who's on the site
 *  right now" row. Keyed by anon_id (person), NOT session_id
 *  (browser session): a person hopping subdomains gets a new
 *  session_id on each origin but the anon_id rides along via the
 *  parent-domain cookie (s124's cross-domain stitching), so per-
 *  session grouping would over-count one visitor as multiple.
 *
 *  The api rolls up per anon_id and returns enough to render the
 *  row — last path the visitor was on, last-seen timestamp, and an
 *  event count for the activity dot. */
export const analyticsActiveSessionSchema = z.object({
    /** Stable per-visitor identifier — the primary key of this row.
     *  Persisted in the SDK as localStorage `mag.anon` (single-origin
     *  installs) or as a cookie on the parent domain (cross-subdomain
     *  installs via data-cookie-domain). */
    anonId: z.string(),
    /** Whatever path the latest event in the window was on — used as
     *  "currently viewing" in the dashboard. */
    lastPath: z.string(),
    /** ISO datetime of the most recent event we have for this visitor. */
    lastSeenAt: z.string(),
    /** Event count for this visitor inside the active window. Drives
     *  the busy/quiet visual on the row. */
    eventsLastWindow: z.number().int().min(0),
    /** Device family pulled from the latest event in the window. Null
     *  for sessions whose events landed before the UA-parse feature
     *  shipped (or for browsers that don't expose userAgent). */
    deviceType: z.string().nullable(),
    browser: z.string().nullable(),
    os: z.string().nullable(),
    /** ISO-3166 alpha-2 country code from the GeoLite2 lookup on the
     *  ingest hop's IP. Null when geo lookup hadn't been wired yet, the
     *  mmdb file isn't present, or the IP didn't resolve (local dev,
     *  127.0.0.1). */
    country: z.string().nullable(),
});
/** A single click point — what the heatmap canvas plots. Coords are
 *  client-space pixels at capture time; the dashboard normalizes by
 *  viewportW/viewportH to a canonical canvas. */
export const analyticsClickPointSchema = z.object({
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    viewportW: z.number().int().min(1),
    viewportH: z.number().int().min(1),
    /** Truncated label so the heatmap can show "what got clicked" as a
     *  tooltip without needing a second query. */
    label: z.string().max(120).nullable(),
});
/** Scroll-depth funnel for one path. percentReached at depth N is the
 *  share of visitors who fired a scroll event with depthPct >= N (so
 *  reaching 50% means reaching 50, NOT only-50). */
export const analyticsScrollFunnelSchema = z.object({
    visitors: z.number().int().min(0),
    /** Indexed by the 4 standard thresholds. Always returns all four
     *  keys, percent 0 when there's no data. */
    percentReached: z.object({
        25: z.number().min(0).max(1),
        50: z.number().min(0).max(1),
        75: z.number().min(0).max(1),
        100: z.number().min(0).max(1),
    }),
});
export const analyticsPathHeatmapSchema = z.object({
    path: z.string(),
    /** The host this heatmap is scoped to, or null when unscoped.
     *
     *  A heatmap is an overlay of ONE rendered page, and /login on app.<apex>
     *  is a different page from /login on the apex. Keying only on pathname
     *  merged them. Scoping fixes that, but scoping SILENTLY would be the same
     *  class of problem, so the host travels with the data and the surface can
     *  name the page it is drawing. */
    host: z.string().nullable(),
    /** Modal viewport across this path's clicks. The canvas uses these as
     *  its aspect ratio so a 1440-wide site doesn't get squashed into a
     *  900x600 box. */
    viewportW: z.number().int().min(1),
    viewportH: z.number().int().min(1),
    clicks: z.array(analyticsClickPointSchema),
    scroll: analyticsScrollFunnelSchema,
});
//# sourceMappingURL=analytics.js.map